import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Loader2 } from "lucide-react";
import { trackPageView, trackScroll } from "./lib/analytics";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { ShopifyAuthProvider } from "./context/ShopifyAuthContext";
import { CustomerAuthProvider } from "./context/CustomerAuthContext";
import { Layout } from "./components/layout/Layout";
import { AuthCallback } from "./components/auth/AuthCallback";

// Eager-loaded: critical shopping funnel (fast first paint for ad traffic)
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CombosPage from "./pages/CombosPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";

// Lazy-loaded: heavy astrology tools (shrinks initial JS bundle)
const HoroscopePage = lazy(() => import("./pages/HoroscopePage"));
const KundliPage = lazy(() => import("./pages/KundliPage"));
const MatchMakingPage = lazy(() => import("./pages/MatchMakingPage"));
const PanchangPage = lazy(() => import("./pages/PanchangPage"));
const NumerologyPage = lazy(() => import("./pages/NumerologyPage"));
const TarotPage = lazy(() => import("./pages/TarotPage"));
const LoveCalculatorPage = lazy(() => import("./pages/LoveCalculatorPage"));
const DoshaPage = lazy(() => import("./pages/DoshaPage"));
const GemRecommendationPage = lazy(() => import("./pages/GemRecommendationPage"));
const DashaPage = lazy(() => import("./pages/DashaPage"));
const VastuCompassPage = lazy(() => import("./pages/VastuCompassPage"));
const VastuToolsPage = lazy(() => import("./pages/VastuToolsPage"));

// Lazy-loaded: informational / low-traffic pages
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsConditionsPage = lazy(() => import("./pages/TermsConditionsPage"));
const RefundPolicyPage = lazy(() => import("./pages/RefundPolicyPage"));
const ShippingPolicyPage = lazy(() => import("./pages/ShippingPolicyPage"));
const MyReportsPage = lazy(() => import("./pages/MyReportsPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));

// Lazy-loaded: SEO landing / blog
const RemediesListPage = lazy(() => import("./pages/remedies/RemediesListPage"));
const RemedyPage = lazy(() => import("./pages/remedies/RemedyPage"));
const BlogListPage = lazy(() => import("./pages/blog/BlogListPage"));
const BlogArticlePage = lazy(() => import("./pages/blog/BlogArticlePage"));

// Lazy-loaded: customer account
const MyAccountPage = lazy(() => import("./pages/MyAccountPage"));
const AccountLoginPage = lazy(() => import("./pages/AccountLoginPage"));

// Lazy-loaded: admin panel (never needed by shoppers)
const SocialDashboard = lazy(() => import("./pages/admin/SocialDashboard"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminAbandonedCarts = lazy(() => import("./pages/admin/AdminAbandonedCarts"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminProductDetail = lazy(() => import("./pages/admin/AdminProductDetail"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminDiscounts = lazy(() => import("./pages/admin/AdminDiscounts"));
const AdminShipping = lazy(() => import("./pages/admin/AdminShipping"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));

// Lightweight suspense fallback
function PageLoader() {
  return (
    <div
      className="flex items-center justify-center min-h-[60vh] w-full"
      data-testid="page-loader"
    >
      <Loader2 className="w-8 h-8 animate-spin text-gold" />
    </div>
  );
}

// Scroll to top on route change + first-party analytics (page views & scroll depth)
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (pathname.startsWith('/admin')) return; // don't track admin panel
    trackPageView(pathname);
    let maxDepth = 0;
    const onScroll = () => {
      const el = document.documentElement;
      const denom = (el.scrollHeight - el.clientHeight) || 1;
      const d = Math.min(100, Math.round((el.scrollTop / denom) * 100));
      if (d > maxDepth) maxDepth = d;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (maxDepth > 0) trackScroll(maxDepth, pathname);
    };
  }, [pathname]);

  return null;
}

// Router with OAuth callback handling
function AppRouter() {
  const location = useLocation();

  // Check URL fragment (not query params) for session_id - Handle OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Layout><HomePage /></Layout>} />
        <Route path="/products" element={<Layout><ProductsPage /></Layout>} />
        <Route path="/combos" element={<Layout><CombosPage /></Layout>} />
        <Route path="/admin/social" element={<SocialDashboard />} />
        <Route path="/product/:id" element={<Layout><ProductDetailPage /></Layout>} />
        <Route path="/about" element={<Layout><AboutPage /></Layout>} />
        <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
        <Route path="/horoscope" element={<Layout><HoroscopePage /></Layout>} />
        <Route path="/kundli" element={<Layout><KundliPage /></Layout>} />
        <Route path="/matchmaking" element={<Layout><MatchMakingPage /></Layout>} />
        <Route path="/panchang" element={<Layout><PanchangPage /></Layout>} />
        <Route path="/numerology" element={<Layout><NumerologyPage /></Layout>} />
        <Route path="/tarot" element={<Layout><TarotPage /></Layout>} />
        <Route path="/love-calculator" element={<Layout><LoveCalculatorPage /></Layout>} />
        <Route path="/dosha" element={<Layout><DoshaPage /></Layout>} />
        <Route path="/gem-recommendation" element={<Layout><GemRecommendationPage /></Layout>} />
        <Route path="/dasha" element={<Layout><DashaPage /></Layout>} />
        <Route path="/vastu-compass" element={<Layout><VastuCompassPage /></Layout>} />
        <Route path="/vastu-tools" element={<Layout><VastuToolsPage /></Layout>} />
        <Route path="/privacy-policy" element={<Layout><PrivacyPolicyPage /></Layout>} />
        <Route path="/terms-conditions" element={<Layout><TermsConditionsPage /></Layout>} />
        <Route path="/refund-policy" element={<Layout><RefundPolicyPage /></Layout>} />
        <Route path="/shipping-policy" element={<Layout><ShippingPolicyPage /></Layout>} />
        <Route path="/my-reports" element={<Layout><MyReportsPage /></Layout>} />
        <Route path="/login" element={<Layout><LoginPage /></Layout>} />
        {/* SEO Landing Pages - Remedies */}
        <Route path="/remedies" element={<Layout><RemediesListPage /></Layout>} />
        <Route path="/remedies/:slug" element={<Layout><RemedyPage /></Layout>} />
        {/* Blog */}
        <Route path="/blog" element={<Layout><BlogListPage /></Layout>} />
        <Route path="/blog/:slug" element={<Layout><BlogArticlePage /></Layout>} />
        {/* Shopify Customer Account */}
        <Route path="/account" element={<Layout><MyAccountPage /></Layout>} />
        <Route path="/account/login" element={<Layout><AccountLoginPage /></Layout>} />
        {/* Own checkout + order confirmation */}
        <Route path="/checkout" element={<Layout><CheckoutPage /></Layout>} />
        <Route path="/order-success/:orderId" element={<Layout><OrderSuccessPage /></Layout>} />
        {/* Admin Panel */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/admin/abandoned" element={<AdminAbandonedCarts />} />
        <Route path="/admin/products" element={<AdminProducts />} />
        <Route path="/admin/products/:id" element={<AdminProductDetail />} />
        <Route path="/admin/categories" element={<AdminCategories />} />
        <Route path="/admin/orders" element={<AdminOrders />} />
        <Route path="/admin/customers" element={<AdminCustomers />} />
        <Route path="/admin/discounts" element={<AdminDiscounts />} />
        <Route path="/admin/shipping" element={<AdminShipping />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <ShopifyAuthProvider>
          <CustomerAuthProvider>
          <CartProvider>
            <BrowserRouter>
              <ScrollToTop />
              <AppRouter />
            </BrowserRouter>
          </CartProvider>
          </CustomerAuthProvider>
        </ShopifyAuthProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
